import {
	BlockManager,
	IBlockData,
	BasicType,
	CUSTOM_LISTING_BLOCK_TYPE,
	DynamicType,
	isDynamicListingColumn,
	resolveBoundListing,
} from '@bay-email/core';

const tempEle = document.createElement('div');
const FOOTER_BLOCK_CLASS = 'bay-email-footer-block';

function isFooterBlock(blockData: IBlockData) {
	if (
		blockData.type !== BasicType.TEXT &&
		blockData.type !== 'advanced_text'
	) {
		return false;
	}

	const isFooterFlag = Boolean((blockData as any)?.data?.value?.isFooterBlock);
	const cssClass = String((blockData as any)?.attributes?.['css-class'] || '');
	const content = String((blockData as any)?.data?.value?.content || '').toLowerCase();

	return (
		isFooterFlag ||
		cssClass.includes(FOOTER_BLOCK_CLASS) ||
		(content.includes('equal housing opportunity') &&
			content.includes('unsubscribe'))
	);
}

/**
 * The listing a scope owner is bound to, named the way the pickers name it.
 *
 * Only the owner carries the binding — its children and their siblings render
 * from it — so without this the wrapper and column are the one place in the
 * tree that never says which listing everything under them is showing.
 *
 * Resolves through the render cache, which fills asynchronously; callers that
 * display this need to repaint on `subscribeListings` or the label stays blank
 * until something else re-renders them.
 */
function getBoundListingLabel(blockData: IBlockData): string {
	const listing = resolveBoundListing(blockData?.data?.value as any);
	if (!listing) return '';

	const label =
		listing.address || listing.title || listing.streetaddress || listing.ml_number;
	return typeof label === 'string' ? label.trim() : '';
}

/**
 * PENDING PRODUCT: the separator and which field to show are unconfirmed. The
 * label prefers address → title → streetaddress → ml_number, which matches how
 * the pickers identify a listing. Change both here — every listing-owning layer
 * routes through this.
 */
function withListing(name: string, blockData: IBlockData): string {
	const listing = getBoundListingLabel(blockData);
	return listing ? `${name} · ${listing}` : name;
}

export function getBlockTitle(blockData: IBlockData, isFromContent = true): string {
	if (blockData.title) return blockData.title;

	if (isFooterBlock(blockData)) {
		return 'Footer';
	}

	if (
		isFromContent &&
		(blockData.type === BasicType.TEXT || blockData.type === BasicType.BUTTON)
	) {
		tempEle.innerHTML = blockData.data.value.content;
		return tempEle.innerText;
	}

	// The listing lives on the column, not the section around it — so the column
	// is what gets named for it. The section stays a plain "Section".
	if (isDynamicListingColumn(blockData)) {
		return withListing('Listing Column', blockData);
	}

	if (blockData.type === CUSTOM_LISTING_BLOCK_TYPE) {
		const name = BlockManager.getBlockByType(blockData.type)?.name || 'Listing Wrapper';
		return withListing(name, blockData);
	}

	if (blockData.type === DynamicType.IMAGE) {
		return 'Dynamic Image';
	}

	if (blockData.type === BasicType.IMAGE || blockData.type === 'advanced_image') {
		const explicitImageType = blockData?.data?.value?.imageType;
		const dynamicTag = String(blockData?.data?.value?.dynamicImageTag || '').trim();
		const isDynamic =
			explicitImageType === 'dynamic' ||
			(explicitImageType !== 'static' && Boolean(dynamicTag));

		return isDynamic ? 'Dynamic Image' : 'Image';
	}

	const blockName = BlockManager.getBlockByType(blockData.type)?.name || '';
	return blockName;
}
